import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useEffect, useState } from "react";
import { api } from "../api/client.ts";
import type { Brand, ImportRecord } from "../api/types.ts";
import { confirmAction, toast } from "../components/feedback.tsx";
import { BrandBadge, Card, Drawer, Empty, TableSkeleton } from "../components/ui.tsx";

interface ListRow {
  id: string;
  name: string;
  members: number;
  isSegment?: boolean;
  filter?: { match: string; conditions: unknown[] } | null;
  brand_id: string;
  brand_name: string;
  brand_badge: string;
  brand_color: string;
  created_at: string;
}

const PAGE = 50;

const VERDICT_TONE: Record<string, string> = {
  valid: "ok",
  risky: "warn",
  bad: "bad",
  pending: "mute",
  catch_all: "warn",
};

export function ImportsPage() {
  const qc = useQueryClient();
  const [openImport, setOpenImport] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<Brand[]>("/config/brands"),
  });
  const lists = useQuery({
    queryKey: ["lists"],
    queryFn: () => api.get<ListRow[]>("/audience/lists"),
  });
  const [offset, setOffset] = useState(0);
  const imports = useQuery({
    queryKey: ["imports", offset],
    queryFn: () =>
      api.get<{ total: number; rows: ImportRecord[] }>(`/imports?limit=${PAGE}&offset=${offset}`),
    // Validation runs in the worker, so progress arrives while this is open.
    refetchInterval: (query) =>
      query.state.data?.rows.some((i) => i.status === "validating") ? 3000 : false,
  });
  const totalImports = imports.data?.total ?? 0;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Lists &amp; imports</h1>
        <p>Upload a CSV; validation runs in the background and only valid rows join the list.</p>
      </div>

      <div className="toolbar">
        <button type="button" className="btn" onClick={() => setUploading(true)}>
          Upload CSV
        </button>
      </div>

      {uploading && (
        <Drawer
          title="Upload a CSV"
          subtitle="Validation runs in the background; only valid rows join the list."
          onClose={() => setUploading(false)}
        >
          <Upload
            brands={brands.data ?? []}
            onDone={() => {
              setUploading(false);
              toast("Uploaded. Validating in the background");
              void qc.invalidateQueries({ queryKey: ["imports"] });
              void qc.invalidateQueries({ queryKey: ["lists"] });
            }}
          />
        </Drawer>
      )}

      {/* Lists first: they are what campaigns are built from, and what this page is mostly visited for. */}
      <ListsTable lists={lists.data ?? []} loading={lists.isLoading} />

      <Card title="Imports">
        {imports.isLoading ? (
          <TableSkeleton />
        ) : imports.data?.rows.length === 0 ? (
          <Empty>Nothing uploaded yet. Use Upload CSV above to bring a list in.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>File</th>
                <th className="num">Rows</th>
                <th className="num">Valid</th>
                <th className="num">Risky</th>
                <th className="num">Bad</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {imports.data?.rows.map((imp) => {
                const done = imp.rowCount - (imp.validCount + imp.riskyCount + imp.badCount);
                return (
                  <tr
                    key={imp.id}
                    onClick={() => setOpenImport(imp.id)}
                    className={openImport === imp.id ? "row-selected" : ""}
                    style={{ cursor: "pointer" }}
                  >
                    <td>
                      <div className="mono" style={{ fontWeight: 600 }}>
                        {imp.filename}
                      </div>
                      <div style={{ color: "var(--gray-500)", fontSize: 11 }}>
                        by {imp.uploadedBy} · {new Date(imp.createdAt).toLocaleString()}
                      </div>
                    </td>
                    <td className="num">{imp.rowCount.toLocaleString()}</td>
                    <td className="num" style={{ color: "var(--green-dark)" }}>
                      {imp.validCount.toLocaleString()}
                    </td>
                    <td className="num" style={{ color: "var(--amber)" }}>
                      {imp.riskyCount.toLocaleString()}
                    </td>
                    <td className="num" style={{ color: "var(--red)" }}>
                      {imp.badCount.toLocaleString()}
                    </td>
                    <td>
                      {imp.status === "validating" ? (
                        <span className="pill info">Validating · {done} left</span>
                      ) : (
                        <span className="pill ok">Ready</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {totalImports > PAGE && (
        <div className="toolbar">
          <span style={{ color: "var(--gray-500)" }}>
            {offset + 1}&ndash;{Math.min(offset + PAGE, totalImports)} of{" "}
            {totalImports.toLocaleString()}
          </span>
          <button
            type="button"
            className="btn ghost small"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(offset - PAGE, 0))}
          >
            &larr; Newer
          </button>
          <button
            type="button"
            className="btn ghost small"
            disabled={offset + PAGE >= totalImports}
            onClick={() => setOffset(offset + PAGE)}
          >
            Older &rarr;
          </button>
        </div>
      )}

      {openImport && <ImportDetail id={openImport} onClose={() => setOpenImport(null)} />}
    </div>
  );
}

function ListsTable({ lists, loading }: { lists: ListRow[]; loading: boolean }) {
  const [segmenting, setSegmenting] = useState(false);
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const brands = useQuery({
    queryKey: ["brands"],
    queryFn: () => api.get<Brand[]>("/config/brands"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/audience/lists/${id}`),
    onSuccess: () => {
      toast("List deleted");
      setError(null);
      void qc.invalidateQueries({ queryKey: ["lists"] });
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Card
      title="Lists"
      subtitle="A list belongs to one brand, because consent and unsubscribes are per brand. A campaign or sequence can only use lists from its own brand."
      action={
        <div className="card-actions">
          <button
            type="button"
            className="btn ghost small"
            onClick={() => setSegmenting((v) => !v)}
          >
            {segmenting ? "Cancel" : "+ New segment"}
          </button>
          <button type="button" className="btn small" onClick={() => setCreating((v) => !v)}>
            {creating ? "Cancel" : "+ New list"}
          </button>
        </div>
      }
    >
      {error && (
        <div className="card-body">
          <div className="error">{error}</div>
        </div>
      )}
      {creating && (
        <NewList
          brands={brands.data ?? []}
          onDone={() => {
            setCreating(false);
            void qc.invalidateQueries({ queryKey: ["lists"] });
          }}
        />
      )}
      {segmenting && (
        <NewSegment
          brands={brands.data ?? []}
          onDone={() => {
            setSegmenting(false);
            void qc.invalidateQueries({ queryKey: ["lists"] });
          }}
        />
      )}
      {loading ? (
        <TableSkeleton cols={3} />
      ) : lists.length === 0 ? (
        <Empty>
          No lists yet. Upload a CSV, build a segment from Pulse stages, or add an empty list to
          fill from Contacts.
        </Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>List</th>
              <th className="num">Contacts</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lists.map((list) => (
              <ListRowView
                key={list.id}
                list={list}
                open={open === list.id}
                onToggle={() => setOpen(open === list.id ? null : list.id)}
                brands={brands.data ?? []}
                onDelete={async () => {
                  if (
                    await confirmAction({
                      title: `Delete ${list.name}?`,
                      message:
                        "The contacts stay; only the list goes. Refused while an unfinished broadcast uses it.",
                      confirmLabel: "Delete list",
                      destructive: true,
                    })
                  )
                    remove.mutate(list.id);
                }}
              />
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function ListRowView({
  list,
  brands,
  open,
  onToggle,
  onDelete,
}: {
  list: ListRow;
  brands: Brand[];
  open: boolean;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [name, setName] = useState(list.name);
  const [brandId, setBrandId] = useState(list.brand_id);
  const [editError, setEditError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api.patch(`/audience/lists/${list.id}`, { name: name.trim(), brandId }),
    onSuccess: () => {
      toast("List saved");
      setEditing(false);
      setEditError(null);
      void qc.invalidateQueries({ queryKey: ["lists"] });
    },
    onError: (err: Error) => setEditError(err.message),
  });
  const members = useQuery({
    queryKey: ["list-members", list.id],
    queryFn: () =>
      api.get<{ id: string; email: string; first_name: string | null; company: string | null }[]>(
        `/audience/lists/${list.id}/members`,
      ),
    enabled: open,
  });
  const removeMember = useMutation({
    mutationFn: (contactId: string) => api.del(`/audience/lists/${list.id}/members/${contactId}`),
    onSuccess: () => {
      toast("Removed from list");
      void qc.invalidateQueries({ queryKey: ["list-members", list.id] });
      void qc.invalidateQueries({ queryKey: ["lists"] });
    },
  });

  return (
    <>
      <tr>
        <td>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <BrandBadge badge={list.brand_badge} color={list.brand_color} />
            {!editing ? (
              <div>
                <div style={{ fontWeight: 600 }}>
                  {list.name}
                  {list.isSegment && (
                    <span className="pill info" style={{ marginLeft: 8 }}>
                      SEGMENT
                    </span>
                  )}
                </div>
                <div style={{ color: "var(--gray-500)", fontSize: 11 }}>
                  {list.brand_name}
                  {list.isSegment && list.filter && (
                    <>
                      {" · "}
                      {list.filter.conditions.length}{" "}
                      {list.filter.conditions.length === 1 ? "condition" : "conditions"}, match{" "}
                      {list.filter.match} · counted live
                    </>
                  )}
                </div>
              </div>
            ) : (
              <form
                className="inline-rename"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (name.trim()) save.mutate();
                }}
              >
                <input
                  aria-label="List name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setEditing(false);
                  }}
                  // biome-ignore lint/a11y/noAutofocus: opened by an explicit Edit click, so focus is expected
                  autoFocus
                  required
                />
                {/* Moving a list between brands: an import lands under whichever
                    brand was chosen at upload, which is easy to get wrong. */}
                <select
                  aria-label="Brand"
                  value={brandId}
                  onChange={(e) => setBrandId(e.target.value)}
                >
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
                <button
                  className="btn small"
                  type="submit"
                  disabled={save.isPending || !name.trim()}
                >
                  Save
                </button>
                <button
                  className="btn ghost small"
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    setName(list.name);
                    setBrandId(list.brand_id);
                    setEditError(null);
                  }}
                >
                  Cancel
                </button>
                {editError && <span className="error-inline">{editError}</span>}
              </form>
            )}
          </div>
        </td>
        <td className="num">{list.members.toLocaleString()}</td>
        <td style={{ textAlign: "right" }}>
          <button type="button" className="btn ghost small" onClick={onToggle}>
            {open ? "Hide" : "Members"}
          </button>{" "}
          <button type="button" className="btn ghost small" onClick={() => setCleaning((v) => !v)}>
            {cleaning ? "Hide" : "Clean"}
          </button>{" "}
          <button
            type="button"
            className="btn ghost small"
            onClick={() => setEditing(true)}
            disabled={editing}
          >
            Edit
          </button>{" "}
          <button type="button" className="btn ghost small" onClick={onDelete}>
            Delete
          </button>
        </td>
      </tr>
      {cleaning && (
        <tr>
          <td colSpan={3} style={{ background: "var(--gray-50)" }}>
            <Hygiene list={list} />
          </td>
        </tr>
      )}
      {open && (
        <tr>
          <td colSpan={3} style={{ background: "var(--gray-50)" }}>
            {members.isLoading ? (
              <TableSkeleton />
            ) : members.data?.length === 0 ? (
              <span style={{ color: "var(--gray-500)" }}>Empty.</span>
            ) : (
              <table>
                <tbody>
                  {members.data?.map((m) => (
                    <tr key={m.id}>
                      <td className="mono">{m.email}</td>
                      <td>{m.first_name ?? "—"}</td>
                      <td>{m.company ?? "—"}</td>
                      <td style={{ textAlign: "right" }}>
                        <button
                          type="button"
                          className="btn ghost small"
                          onClick={() => removeMember.mutate(m.id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {list.members > 100 && <p className="hint">Showing the 100 most recently added.</p>}
          </td>
        </tr>
      )}
    </>
  );
}

function ImportDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useQuery({
    queryKey: ["import", id],
    queryFn: () => api.get<ImportRecord>(`/imports/${id}`),
    refetchInterval: (query) => (query.state.data?.status === "validating" ? 3000 : false),
  });

  if (!detail.data) {
    return (
      <Drawer title="Loading…" onClose={onClose}>
        <p style={{ color: "var(--gray-500)" }}>Loading import…</p>
      </Drawer>
    );
  }
  const d = detail.data;

  return (
    <Drawer
      title={<span className="mono">{d.filename}</span>}
      subtitle={`${d.status} · ${d.rowCount.toLocaleString()} rows · ${d.validCount.toLocaleString()} valid, ${d.riskyCount.toLocaleString()} risky, ${d.badCount.toLocaleString()} bad · uploaded ${new Date(d.createdAt).toLocaleString()} by ${d.uploadedBy}`}
      onClose={onClose}
    >
      <Card title="Sample rows" subtitle="First 25 rows and how each was classified">
        {detail.data.rowsPrunedAt && (
          <div className="card-body">
            <p className="caveat" style={{ margin: 0 }}>
              The rows of this upload were cleared on{" "}
              {new Date(detail.data.rowsPrunedAt).toLocaleDateString()}. Its counts are kept, and
              the contacts it created are on the list; only the copy of the file's own rows is gone.
            </p>
          </div>
        )}
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Verdict</th>
              <th>Why</th>
            </tr>
          </thead>
          <tbody>
            {detail.data.sample?.map((row, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: a fixed sample that never reorders, and a file may legitimately repeat an address.
              <tr key={`${row.email ?? "row"}-${i}`}>
                <td className="mono">
                  {row.email ?? <em style={{ color: "var(--gray-400)" }}>none</em>}
                </td>
                <td>
                  <span className={`pill ${VERDICT_TONE[row.validation] ?? "mute"}`}>
                    {row.validation}
                  </span>
                </td>
                <td style={{ color: "var(--gray-500)" }}>{row.detail ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Drawer>
  );
}

/** An empty list, for filling from the Contacts page rather than a CSV. */
function NewList({ brands, onDone }: { brands: Brand[]; onDone: () => void }) {
  const [name, setName] = useState("");
  const [brandId, setBrandId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post("/audience/lists", { name: name.trim(), brandId: brandId || brands[0]?.id }),
    onSuccess: () => {
      onDone();
      toast("List created");
    },
    onError: (err: Error) => setError(err.message),
  });

  if (brands.length === 0) {
    return (
      <div className="card-body">
        <div className="error">Add a brand first: every list belongs to one.</div>
      </div>
    );
  }

  return (
    <form
      className="card-body"
      style={{ background: "var(--gray-50)" }}
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        create.mutate();
      }}
    >
      {error && <div className="error">{error}</div>}
      <div className="row">
        <div className="field">
          <label htmlFor="nl-name">List name</label>
          <input id="nl-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="nl-brand">Brand</label>
          <select
            id="nl-brand"
            value={brandId || brands[0]?.id}
            onChange={(e) => setBrandId(e.target.value)}
          >
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <div className="hint">Only this brand's campaigns and sequences can use it.</div>
        </div>
      </div>
      <button className="btn small" type="submit" disabled={create.isPending || !name.trim()}>
        Create list
      </button>
    </form>
  );
}

function Upload({ brands, onDone }: { brands: Brand[]; onDone: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [brandId, setBrandId] = useState("");
  const [listName, setListName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const upload = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.set("file", file!);
      form.set("brandId", brandId || brands[0]!.id);
      form.set("uploadedBy", "web");
      if (listName) form.set("listName", listName);

      // FormData sets its own multipart boundary, so this bypasses the JSON
      // client rather than fighting its content-type.
      const res = await fetch("/api/imports", {
        method: "POST",
        body: form,
        credentials: "same-origin",
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(body?.error ?? "upload failed");
      }
      return res.json();
    },
    onSuccess: () => {
      setFile(null);
      setListName("");
      onDone();
    },
    onError: (err: Error) => setError(err.message),
  });

  if (brands.length === 0) {
    return (
      <Card>
        <div className="card-body">
          Create a brand under Domains &amp; senders before importing contacts.
        </div>
      </Card>
    );
  }

  return (
    <Card title="Upload a CSV">
      <form
        className="card-body"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setError(null);
          if (file) upload.mutate();
        }}
      >
        {error && <div className="error">{error}</div>}

        {/* biome-ignore lint/a11y/noStaticElementInteractions: the drop zone wraps a real file input, which remains the keyboard path. */}
        <div
          className={`dropzone${dragging ? " over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const dropped = e.dataTransfer.files[0];
            if (dropped) setFile(dropped);
          }}
        >
          {file ? (
            <>
              <strong className="mono">{file.name}</strong>
              <p>{(file.size / 1024).toFixed(1)} KB</p>
            </>
          ) : (
            <p>Drop a CSV here, or choose a file.</p>
          )}
          <input
            type="file"
            accept=".csv,text/csv,text/tab-separated-values"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>

        <div className="row" style={{ marginTop: 14 }}>
          <div className="field">
            <label htmlFor="i-brand">Brand</label>
            <select id="i-brand" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              {brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
            <div className="hint">
              The list this creates belongs to this brand, and only its campaigns and sequences can
              use it. You can move the list to another brand later.
            </div>
          </div>
          <div className="field">
            <label htmlFor="i-list">List name</label>
            <input
              id="i-list"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="defaults to the filename"
            />
          </div>
        </div>

        <div className="toolbar" style={{ marginBottom: 0 }}>
          <button className="btn" type="submit" disabled={!file || upload.isPending}>
            {upload.isPending ? "Uploading…" : "Upload and validate"}
          </button>
          {/* Served by the API from the importer's own header table, so it
              cannot drift from what is actually recognised. */}
          <a className="btn ghost" href="/api/imports/template.csv" download>
            Download CSV template
          </a>
        </div>
        <p className="caveat" style={{ margin: "12px 0 0" }}>
          The template&rsquo;s two sample rows use <span className="mono">example.com</span> and{" "}
          <span className="mono">example.org</span>, reserved domains that accept no mail, so they
          validate as bad — replace them with your own contacts. Recognised columns:{" "}
          <span className="mono">Email</span> (required), <span className="mono">First Name</span>,{" "}
          <span className="mono">Last Name</span>, <span className="mono">Company</span>,{" "}
          <span className="mono">Title</span>, <span className="mono">Phone</span> — common variants
          like &ldquo;Email Address&rdquo; or &ldquo;Job Title&rdquo; work too. Any other column is
          kept as a custom field you can use as a merge tag. Only rows that validate join the list.
        </p>
      </form>
    </Card>
  );
}

interface SegmentField {
  field: string;
  label: string;
  kind: "text" | "date";
  ops: string[];
}

const OP_LABEL: Record<string, string> = {
  is: "is",
  is_not: "is not",
  contains: "contains",
  not_contains: "does not contain",
  starts_with: "starts with",
  is_set: "is set",
  is_not_set: "is not set",
  in_last_days: "in the last … days",
  not_in_last_days: "not in the last … days",
};

const NO_VALUE = new Set(["is_set", "is_not_set"]);

/**
 * Building a segment: a list whose membership is a question.
 *
 * The match count is live and the save is disabled until the conditions are
 * complete, because the failure mode of every filter builder is a segment that
 * silently matches nobody — or everybody — and the number is the only thing
 * that tells you which one you have just built.
 */
/** Stable identity per row, so removing one does not reshuffle the others. */
function newCondition() {
  return { uid: crypto.randomUUID(), field: "company", op: "contains", value: "" };
}

/**
 * A condition's value. Dispositions are picked by Pulse's own names — nobody
 * should have to know that "Closed - DNC" is stored as closed_do_not_contact.
 * Partial-match operators still take free text.
 */
function ConditionValue({
  field,
  op,
  kind,
  value,
  onChange,
}: {
  field: string;
  op: string;
  kind: SegmentField["kind"] | undefined;
  value: string;
  onChange: (value: string) => void;
}) {
  const facets = useQuery({
    queryKey: ["pulse-facets"],
    queryFn: () =>
      api.get<{ dispositions?: { value: string; n: number; label: string }[] }>(
        "/audience/pulse-facets",
      ),
    staleTime: 5 * 60_000,
    enabled: field === "pulse_disposition",
  });
  if (field === "pulse_disposition" && (op === "is" || op === "is_not")) {
    const choices = facets.data?.dispositions ?? [];
    return (
      <select className="seg-part" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose a disposition…</option>
        {value && !choices.some((d) => d.value === value) && <option value={value}>{value}</option>}
        {choices.map((d) => (
          <option key={d.value} value={d.value}>
            {d.label} ({d.n.toLocaleString()})
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      className="seg-part"
      type={kind === "date" ? "number" : "text"}
      value={value}
      placeholder={kind === "date" ? "days" : "value"}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function NewSegment({ brands, onDone }: { brands: Brand[]; onDone: () => void }) {
  const [name, setName] = useState("");
  const [brandId, setBrandId] = useState("");
  const [match, setMatch] = useState<"all" | "any">("all");
  const [conditions, setConditions] = useState<
    { uid: string; field: string; op: string; value: string }[]
  >(() => [newCondition()]);
  const [error, setError] = useState<string | null>(null);

  const fields = useQuery({
    queryKey: ["segment-fields"],
    queryFn: () => api.get<SegmentField[]>("/audience/segment-fields"),
  });

  useEffect(() => {
    if (!brandId && brands[0]) setBrandId(brands[0].id);
  }, [brandId, brands]);

  const complete = conditions.filter((c) => c.field && (NO_VALUE.has(c.op) || c.value.trim()));
  const payload = {
    match,
    conditions: complete.map((c) => ({
      field: c.field,
      op: c.op,
      ...(NO_VALUE.has(c.op) ? {} : { value: c.value.trim() }),
    })),
  };

  const preview = useMutation({
    mutationFn: () =>
      api.post<{ matches: number; sample: SampleMatch[] }>("/audience/lists/preview", {
        brandId,
        filter: payload,
      }),
  });

  /**
   * Debounced: every keystroke would otherwise be a count over every contact.
   * Keyed on the serialised request rather than the object, which is rebuilt
   * each render and would restart the timer forever.
   */
  const requestKey = JSON.stringify({ brandId, payload });
  const runPreview = preview.mutate;
  useEffect(() => {
    const request = JSON.parse(requestKey) as {
      brandId: string;
      payload: { conditions: unknown[] };
    };
    if (!request.brandId || request.payload.conditions.length === 0) return;
    const timer = setTimeout(() => runPreview(), 400);
    return () => clearTimeout(timer);
  }, [requestKey, runPreview]);

  const create = useMutation({
    mutationFn: () => api.post("/audience/lists", { brandId, name: name.trim(), filter: payload }),
    onSuccess: () => {
      onDone();
      toast("List created");
    },
    onError: (err: Error) => setError(err.message),
  });

  const defFor = (field: string) => fields.data?.find((f) => f.field === field);

  return (
    <div className="card-body">
      {error && <div className="error">{error}</div>}

      <div className="row">
        <div className="field">
          <label htmlFor="seg-name">Name</label>
          <input
            id="seg-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Closed for peptides, never replied"
          />
        </div>
        <div className="field">
          <label htmlFor="seg-brand">Brand</label>
          <select id="seg-brand" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        {/* biome-ignore lint/a11y/noLabelWithoutControl: heading for the rows below. */}
        <label>Conditions</label>
        <div className="daypicker" style={{ marginBottom: 8 }}>
          {(["all", "any"] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={`day${match === m ? " on" : ""}`}
              onClick={() => setMatch(m)}
            >
              match {m}
            </button>
          ))}
        </div>

        {conditions.map((condition, i) => {
          const def = defFor(condition.field);
          return (
            <div className="seg-row" key={condition.uid}>
              <select
                className="seg-part"
                value={condition.field}
                onChange={(e) => {
                  const next = [...conditions];
                  const ops = defFor(e.target.value)?.ops ?? [];
                  next[i] = {
                    uid: condition.uid,
                    field: e.target.value,
                    // Dispositions are picked from a list, which "is" offers.
                    op:
                      e.target.value === "pulse_disposition"
                        ? "is"
                        : ops.includes(condition.op)
                          ? condition.op
                          : (ops[0] ?? "is"),
                    value: "",
                  };
                  setConditions(next);
                }}
              >
                {fields.data?.map((f) => (
                  <option key={f.field} value={f.field}>
                    {f.label}
                  </option>
                ))}
              </select>

              <select
                className="seg-part"
                value={condition.op}
                onChange={(e) => {
                  const next = [...conditions];
                  next[i] = { ...condition, op: e.target.value };
                  setConditions(next);
                }}
              >
                {(def?.ops ?? []).map((op) => (
                  <option key={op} value={op}>
                    {OP_LABEL[op] ?? op}
                  </option>
                ))}
              </select>

              {!NO_VALUE.has(condition.op) && (
                <ConditionValue
                  field={condition.field}
                  op={condition.op}
                  kind={def?.kind}
                  value={condition.value}
                  onChange={(value) => {
                    const next = [...conditions];
                    next[i] = { ...condition, value };
                    setConditions(next);
                  }}
                />
              )}

              {conditions.length > 1 && (
                <button
                  type="button"
                  className="btn ghost small"
                  onClick={() => setConditions(conditions.filter((_, idx) => idx !== i))}
                >
                  ✕
                </button>
              )}
            </div>
          );
        })}

        <button
          type="button"
          className="btn ghost small"
          onClick={() => setConditions([...conditions, newCondition()])}
        >
          + Add condition
        </button>
      </div>

      <div className="hint" style={{ marginBottom: 10 }}>
        {complete.length === 0
          ? "A segment with no complete conditions matches nobody."
          : preview.isPending
            ? "Counting…"
            : preview.data
              ? `${preview.data.matches.toLocaleString()} contacts match right now. Re-checked every time it is used.`
              : " "}
      </div>

      {complete.length > 0 && preview.data && preview.data.sample.length > 0 && (
        <SamplePreview sample={preview.data.sample} total={preview.data.matches} />
      )}

      <button
        type="button"
        className="btn"
        disabled={!name.trim() || !brandId || complete.length === 0 || create.isPending}
        onClick={() => create.mutate()}
      >
        {create.isPending ? "Saving…" : "Save segment"}
      </button>
    </div>
  );
}

interface Finding {
  category: string;
  label: string;
  note: string;
  count: number;
  sample: string[];
}

interface Report {
  members: number;
  findings: Finding[];
  checksDomains: boolean;
  isSegment: boolean;
}

/**
 * What is wrong with a list, and taking it off.
 *
 * Nothing is pre-ticked. Some of these are obviously dead weight and some are
 * a judgement call — a role address is often the only address a small merchant
 * has — so the choice is the operator's every time rather than a default they
 * have to notice and undo.
 */
function Hygiene({ list }: { list: ListRow }) {
  const qc = useQueryClient();
  const [chosen, setChosen] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [removed, setRemoved] = useState<number | null>(null);

  const report = useQuery({
    queryKey: ["hygiene", list.id],
    queryFn: () => api.get<Report>(`/audience/lists/${list.id}/hygiene`),
  });

  const clean = useMutation({
    mutationFn: () =>
      api.post<{ removed: number }>(`/audience/lists/${list.id}/clean`, { categories: chosen }),
    onSuccess: (res) => {
      setError(null);
      setRemoved(res.removed);
      setChosen([]);
      void qc.invalidateQueries({ queryKey: ["hygiene", list.id] });
      void qc.invalidateQueries({ queryKey: ["lists"] });
      void qc.invalidateQueries({ queryKey: ["list-members", list.id] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const r = report.data;
  const selectedCount = (r?.findings ?? [])
    .filter((f) => chosen.includes(f.category))
    .reduce((sum, f) => sum + f.count, 0);

  if (report.isPending) return <span style={{ color: "var(--gray-500)" }}>Checking…</span>;
  if (!r) return <span style={{ color: "var(--gray-500)" }}>Could not check this list.</span>;

  return (
    <div style={{ display: "grid", gap: 10 }}>
      {error && <div className="error">{error}</div>}
      {removed !== null && (
        <div className="notice">
          Removed {removed.toLocaleString()} from the list. The contacts themselves are untouched.
        </div>
      )}

      {r.findings.length === 0 ? (
        <div style={{ color: "var(--gray-500)" }}>
          Nothing to clean — all {r.members.toLocaleString()} members look fine.
        </div>
      ) : (
        <>
          {r.findings.map((f) => (
            <label key={f.category} className="hygiene-row">
              <input
                className="hygiene-check"
                type="checkbox"
                checked={chosen.includes(f.category)}
                disabled={r.isSegment}
                onChange={(e) =>
                  setChosen(
                    e.target.checked
                      ? [...chosen, f.category]
                      : chosen.filter((c) => c !== f.category),
                  )
                }
              />
              <span>
                <strong>
                  {f.count.toLocaleString()} · {f.label}
                </strong>
                <span className="hygiene-note">{f.note}</span>
                <span className="hygiene-sample mono">{f.sample.join("  ")}</span>
              </span>
            </label>
          ))}

          {r.isSegment ? (
            <div className="hint">
              This is a segment, so its membership comes from its conditions rather than a stored
              set. Fix it by editing the conditions.
            </div>
          ) : (
            <button
              type="button"
              className="btn small"
              disabled={chosen.length === 0 || clean.isPending}
              onClick={() => clean.mutate()}
            >
              {clean.isPending
                ? "Removing…"
                : `Remove ${selectedCount.toLocaleString()} from the list`}
            </button>
          )}
        </>
      )}

      <div className="hint">
        Checks suppression, consent, syntax, disposable providers, role addresses and engagement.
        {!r.checksDomains &&
          " Whether a domain still accepts mail is checked at import, not here — it costs a DNS lookup per domain and belongs in the worker."}
      </div>
    </div>
  );
}

interface SampleMatch {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  pulse_stage: string | null;
  pulse_status: string | null;
}

/**
 * Who the segment catches, not only how many: a condition that is wrong
 * shows up as the wrong people far sooner than as a surprising number.
 */
function SamplePreview({ sample, total }: { sample: SampleMatch[]; total: number }) {
  return (
    <div className="sample-preview">
      <div className="label-mono" style={{ marginBottom: 6 }}>
        {total > sample.length
          ? `First ${sample.length} of ${total.toLocaleString()}`
          : `All ${sample.length}`}
      </div>
      <table>
        <tbody>
          {sample.map((m) => (
            <tr key={m.id}>
              <td>
                <div style={{ fontWeight: 600 }}>
                  {[m.first_name, m.last_name].filter(Boolean).join(" ") || "—"}
                </div>
                <div className="mono" style={{ color: "var(--gray-500)", fontSize: 11 }}>
                  {m.email}
                </div>
              </td>
              <td>{m.company ?? "—"}</td>
              <td>
                {m.pulse_stage ? (
                  <span className={`pill ${m.pulse_stage === "lost" ? "mute" : "ok"}`}>
                    {m.pulse_stage.replaceAll("_", " ")}
                  </span>
                ) : (
                  "—"
                )}
                {m.pulse_status && <div className="hint">Sub-status: {m.pulse_status}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
